import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../cn';
import { Spinner } from './spinner';

/** Классы — audiobooks/web/src/components/ui/button.tsx (radix-luma) с префиксом `nd:`. */
const buttonVariants = cva(
  "nd:inline-flex nd:shrink-0 nd:items-center nd:justify-center nd:rounded-4xl nd:border nd:border-transparent nd:bg-clip-padding nd:text-sm nd:font-medium nd:whitespace-nowrap nd:press nd:outline-none nd:select-none nd:focus-visible:border-ring nd:focus-visible:ring-3 nd:focus-visible:ring-ring/30 nd:disabled:pointer-events-none nd:disabled:opacity-50 nd:[&_svg]:pointer-events-none nd:[&_svg]:shrink-0 nd:[&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      // Спек «Кнопки нового дизайна: одна шкала» — таблица «Стили».
      variant: {
        // Открытая всплывашка — как наведение, без затемнения: у всех лаймовых одинаково
        default: 'nd:bg-primary nd:text-primary-foreground nd:hover:bg-primary/80 nd:aria-expanded:bg-primary/80',
        // foreground 8 % — подложка видна и на белой карточке, и в тёмной колонке, и на сером фоне заметки
        ghost: 'nd:hover:bg-foreground/8 nd:hover:text-foreground nd:aria-expanded:bg-foreground/8',
        danger:
          'nd:text-muted-foreground nd:hover:bg-destructive/10 nd:hover:text-destructive nd:aria-expanded:bg-destructive/10 nd:aria-expanded:text-destructive',
        accent: 'nd:text-primary-text nd:hover:bg-primary/16 nd:aria-expanded:bg-primary/16',
        outline:
          'nd:border-border nd:bg-background nd:hover:bg-foreground/6 nd:aria-expanded:bg-foreground/6 nd:dark:bg-transparent nd:dark:hover:bg-foreground/6',
        contrast: 'nd:bg-foreground nd:text-background nd:hover:bg-foreground/85',
        error: 'nd:text-destructive nd:hover:bg-destructive/10 nd:aria-expanded:bg-destructive/10',
        // Серая подложка и приглушённая иконка, как капсула ViewToggle: круглые кнопки шапки списка проектов
        soft: 'nd:bg-muted nd:text-foreground/55 nd:hover:bg-foreground/14 nd:hover:text-foreground nd:aria-expanded:bg-foreground/14 nd:aria-expanded:text-foreground',
      },
      // Других размеров нет: своя высота по месту не задаётся.
      size: {
        lg: 'nd:h-12 nd:gap-1.5 nd:px-4 nd:font-bold',
        default: 'nd:h-9 nd:gap-1.5 nd:px-3',
        sm: 'nd:h-8 nd:gap-1 nd:px-3',
        // Капсула шапки списка проектов: значок и число (фильтр включён) — высота как у icon-compact рядом
        compact: "nd:h-7 nd:gap-1 nd:pr-2.25 nd:pl-2 nd:text-xs nd:[&_svg:not([class*='size-'])]:size-3.5",
        xs: "nd:h-6 nd:gap-1 nd:px-2.5 nd:text-xs nd:[&_svg:not([class*='size-'])]:size-3",
        // press-icon: у 24–32 px общее 0.97 — полпикселя с края, нажатия не видно
        // Кнопки-значки: в покое круг (высота и наименьшая ширина), с подписью (RevealButton) растут вширь.
        // Поля под значок: 28 = 14 + 6 + 6 + рамка 1 + 1; 32 — значок до 20 px; 24 — значок 12 px
        'icon-compact': "nd:h-7 nd:min-w-7 nd:px-1.5 nd:press-icon nd:[&_svg:not([class*='size-'])]:size-3.5",
        'icon-sm': 'nd:h-8 nd:min-w-8 nd:px-1.25 nd:press-icon',
        'icon-xs': "nd:h-6 nd:min-w-6 nd:px-1.25 nd:press-icon nd:[&_svg:not([class*='size-'])]:size-3",
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

/** Подложка открытой всплывашки у слоя: те же цвета, что aria-expanded у вариантов, но атрибут стоит на рамке */
const LAYER_EXPANDED: Record<ButtonVariant, string> = {
  default: 'nd:group-aria-expanded/btn:bg-primary/80',
  contrast: '',
  ghost: 'nd:group-aria-expanded/btn:bg-foreground/8',
  danger: 'nd:group-aria-expanded/btn:bg-destructive/10 nd:group-aria-expanded/btn:text-destructive',
  accent: 'nd:group-aria-expanded/btn:bg-primary/16',
  outline: 'nd:group-aria-expanded/btn:bg-foreground/6',
  error: 'nd:group-aria-expanded/btn:bg-destructive/10',
  soft: 'nd:group-aria-expanded/btn:bg-foreground/14 nd:group-aria-expanded/btn:text-foreground',
};

/** Рамка: размер, фокус и зона нажатия; сама не масштабируется. size-fit — во flex-родителе не растягивается (своей высоты нет) */
const LAYER_FRAME = 'nd:group/btn nd:inline-flex nd:size-fit nd:shrink-0 nd:rounded-4xl nd:outline-none nd:select-none nd:disabled:pointer-events-none';
/** Видимый слой: сжимается при нажатии на рамку (и мышью, и пробелом), кольцо фокуса и бледность выключенной — от рамки */
const LAYER_VISUAL =
  'nd:group-active/btn:scale-(--press-scale) nd:group-focus-visible/btn:border-ring nd:group-focus-visible/btn:ring-3 nd:group-focus-visible/btn:ring-ring/30 nd:group-disabled/btn:opacity-50';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Кнопка из двух слоёв: рамка неподвижна, при нажатии сжимается видимый слой внутри. Для кнопок, к которым
   * прикреплена всплывашка (меню, вопрос): Radix ставит её по рамке и следит за ней — у обычной кнопки сжатие и возврат
   * сдвигали бы только что открытое меню. Заодно зона нажатия не уменьшается под пальцем. className — на рамку.
   * С asChild не сочетается.
   */
  layered?: boolean;
  /**
   * Идёт действие кнопки: по центру лоадер, надпись или значок — прозрачными (ширина кнопки та же, имя для чтеца то же),
   * кнопка недоступна. С asChild не сочетается.
   */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, layered = false, loading = false, type, children, disabled, ...props }, ref) => {
    const content = loading ? (
      <>
        <span className="nd:inline-flex nd:items-center nd:gap-[inherit] nd:opacity-0">{children}</span>
        <span className="nd:absolute nd:inset-0 nd:grid nd:place-items-center">
          <Spinner />
        </span>
      </>
    ) : (
      children
    );
    const busy = loading ? { 'aria-busy': true as const } : {};
    if (layered) {
      return (
        <button ref={ref} type={type ?? 'button'} data-slot="button" className={cn(LAYER_FRAME, className)} disabled={disabled || loading} {...busy} {...props}>
          <span className={cn(buttonVariants({ variant, size }), LAYER_EXPANDED[variant ?? 'default'], LAYER_VISUAL, loading && 'nd:relative nd:group-disabled/btn:opacity-100')}>{content}</span>
        </button>
      );
    }
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : (type ?? 'button')}
        data-slot="button"
        // Идёт действие — кнопка недоступна, но не бледная: она занята, а не выключена
        className={cn(buttonVariants({ variant, size }), loading && 'nd:relative nd:disabled:opacity-100', className)}
        disabled={disabled || loading}
        {...busy}
        {...props}
      >
        {asChild ? children : content}
      </Comp>
    );
  }
);
Button.displayName = 'Button';
